import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  isAnySectionCutByBudget,
  isAnySectionFailed,
  isAnySectionPending,
  NO_COMPANY_RUN,
  type CompanySectionsState,
} from "@/contexts/company-sections-context";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { CompanyDTO, type CompanySectionKey } from "@shaarp/common";
import { StarsIcon } from "lucide-react";
import { useEffect, useRef } from "react";

/**
 * How long the pointer rests on a tab before it reads as reaching for it: a
 * pointer crossing the bar on its way to another tab is no intent.
 */
export const TAB_INTENT_HOVER_MS = 200;

export type TabConfig = {
  readonly value: string;
  readonly hasIcon?: boolean;
  readonly i18nKey?: string;
  readonly adminOnly?: boolean;
  /** Shows the people of the company: hidden once direct contact is cut (#757). */
  readonly showsPeople?: boolean;
  readonly isAvailable?: (
    company: CompanyDTO,
    arePeopleVisible: boolean,
  ) => boolean;
  readonly disabledTooltipKey?: string;
  /**
   * The sections the tab's cards are filled from: while one runs, the tab
   * stays open on its skeletons rather than reading as having no data — and
   * once one failed or was cut by the spent budget, as nothing was read.
   */
  readonly filledFrom?: readonly CompanySectionKey[];
};

type CompanyTabsNavigationProps = {
  tabs: readonly TabConfig[];
  activeTab: string;
  defaultTab: string;
  onTabChange: (value: string) => void;
  /**
   * The pointer rested on an open tab's trigger, or the focus reached it: the
   * user is about to open it, so its reads may be prefetched.
   */
  onTabIntent?: (value: string) => void;
  /** The pointer left the tab's trigger. */
  onTabIntentEnd?: (value: string) => void;
  company: CompanyDTO;
  /** The run filling the company, for the tabs whose sections still run. */
  sections?: CompanySectionsState;
  isAdminMode?: boolean;
  compact?: boolean;
  className?: string;
};

/** Shared tab navigation used in both the sticky compact header and the main tab bar. */
export function CompanyTabsNavigation({
  tabs,
  activeTab,
  defaultTab,
  onTabChange,
  onTabIntent,
  onTabIntentEnd,
  company,
  sections = NO_COMPANY_RUN,
  isAdminMode = false,
  compact = false,
  className,
}: CompanyTabsNavigationProps) {
  const { t } = useTranslation();
  const arePeopleVisible = useArePeopleVisible();
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  const visibleTabs = tabs.filter(
    (tab) =>
      (!tab.adminOnly || isAdminMode) && (!tab.showsPeople || arePeopleVisible)
  );

  return (
    <Tabs
      value={activeTab}
      defaultValue={defaultTab}
      onValueChange={onTabChange}
      className={className}
    >
      <TabsList className="inline-flex justify-center h-auto gap-2 mx-auto min-h-10 overflow-x-auto max-w-full">
        {visibleTabs.map((tab) => {
          const filledFrom = tab.filledFrom ?? [];
          // Closed only on sections that read the company and found nothing:
          // its tooltip says the company has no such data.
          const isDisabled =
            !!tab.isAvailable &&
            !tab.isAvailable(company, arePeopleVisible) &&
            !isAnySectionPending(sections, filledFrom) &&
            !isAnySectionFailed(sections, filledFrom) &&
            !isAnySectionCutByBudget(sections, filledFrom);

          const trigger = (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              disabled={isDisabled}
              onPointerEnter={
                isDisabled
                  ? undefined
                  : () => {
                      clearTimeout(hoverTimer.current);
                      hoverTimer.current = setTimeout(
                        () => onTabIntent?.(tab.value),
                        TAB_INTENT_HOVER_MS,
                      );
                    }
              }
              onPointerLeave={
                isDisabled
                  ? undefined
                  : () => {
                      clearTimeout(hoverTimer.current);
                      onTabIntentEnd?.(tab.value);
                    }
              }
              onFocus={isDisabled ? undefined : () => onTabIntent?.(tab.value)}
              className={cn(
                compact
                  ? "text-sm px-3 py-1.5"
                  : "text-sm sm:text-base px-3 py-1.5",
                tab.adminOnly &&
                  "bg-warning/20 border border-warning data-[state=active]:bg-warning data-[state=active]:text-warning-foreground text-foreground disabled:opacity-100 disabled:text-foreground disabled:pointer-events-auto disabled:cursor-not-allowed",
              )}
            >
              {tab.hasIcon && (
                <StarsIcon
                  className={
                    compact
                      ? "mr-1.5 w-4 h-4"
                      : "mr-1.5 w-4 h-4 sm:mr-2 sm:w-[18px] sm:h-[18px]"
                  }
                />
              )}
              {t(
                `company.tabs.${tab.i18nKey ?? tab.value}` as Parameters<
                  typeof t
                >[0],
              )}
            </TabsTrigger>
          );

          if (isDisabled && tab.disabledTooltipKey) {
            return (
              <TooltipProvider key={tab.value} delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex cursor-help">{trigger}</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t(
                      `company.tabs.${tab.disabledTooltipKey}` as Parameters<typeof t>[0],
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }

          return trigger;
        })}
      </TabsList>
    </Tabs>
  );
}
